<template>
  <div class="star-background" aria-hidden="true">
    <div class="star-background-gradient"></div>
    <div v-if="useMotionBackground" ref="canvasContainer" class="star-background-canvas"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const canvasContainer = ref(null);
const useMotionBackground = ref(false);

let isMobileViewport = false;

let THREE;
let OrbitControls;
let scene;
let camera;
let renderer;
let controls;
let particles;
let infinityEmblem;
let mouseX = 0;
let mouseY = 0;
let animationFrame;
let isMounted = true;

const shouldUseThree = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const updatePointer = (clientX, clientY) => {
  mouseX = (clientX / window.innerWidth) * 2 - 1;
  mouseY = -(clientY / window.innerHeight) * 2 + 1;
};

const createParticleTexture = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.arc(16, 16, 16, 0, Math.PI * 2);
  ctx.fill();
  return new THREE.CanvasTexture(canvas);
};

const createInfinityCurve = (scale = 2.6) => {
  class InfinityCurve extends THREE.Curve {
    constructor() {
      super();
      this.scale = scale;
    }

    getPoint(t) {
      const angle = t * Math.PI * 2;
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);
      return new THREE.Vector3(this.scale * sin, this.scale * sin * cos, 0);
    }
  }

  return new InfinityCurve();
};

const initThree = async () => {
  const threeModule = await import('three');
  const orbitModule = await import('three/examples/jsm/controls/OrbitControls.js');

  if (!isMounted || !canvasContainer.value) return;

  THREE = threeModule;
  OrbitControls = orbitModule.OrbitControls;

  isMobileViewport = window.innerWidth < 768;

  scene = new THREE.Scene();
  scene.background = null;
  scene.fog = new THREE.Fog(0x0a0a0a, 10, 50);

  camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.position.set(0, 2, 12);

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobileViewport ? 1.5 : 2));
  canvasContainer.value.appendChild(renderer.domElement);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.autoRotate = true;
  controls.autoRotateSpeed = isMobileViewport ? 0.35 : 0.6;
  controls.enableZoom = false;
  controls.enablePan = false;

  scene.add(new THREE.AmbientLight(0x404060));

  const dirLight = new THREE.DirectionalLight(0xffffff, 1);
  dirLight.position.set(1, 2, 1);
  scene.add(dirLight);

  const pointLight1 = new THREE.PointLight(0x3b82f6, 1, 20);
  pointLight1.position.set(2, 3, 4);
  scene.add(pointLight1);

  const pointLight2 = new THREE.PointLight(0x10b981, 1, 20);
  pointLight2.position.set(-2, -1, 4);
  scene.add(pointLight2);

  const particleCount = isMobileViewport ? 400 : 800;
  const particleGeometry = new THREE.BufferGeometry();
  const posArray = new Float32Array(particleCount * 3);
  const colorArray = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount * 3; i += 3) {
    const r = 15 + Math.random() * 10;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.random() * Math.PI * 2;

    posArray[i] = r * Math.sin(theta) * Math.cos(phi);
    posArray[i + 1] = r * Math.sin(theta) * Math.sin(phi);
    posArray[i + 2] = r * Math.cos(theta);

    const color = new THREE.Color().setHSL(Math.random(), 0.6, 0.5);
    colorArray[i] = color.r;
    colorArray[i + 1] = color.g;
    colorArray[i + 2] = color.b;
  }

  particleGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
  particleGeometry.setAttribute('color', new THREE.BufferAttribute(colorArray, 3));

  particles = new THREE.Points(
    particleGeometry,
    new THREE.PointsMaterial({
      size: 0.12,
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      map: createParticleTexture(),
    })
  );
  scene.add(particles);

  const infinityCurve = createInfinityCurve();
  const infinityGeometry = new THREE.TubeGeometry(
    infinityCurve,
    isMobileViewport ? 120 : 160,
    isMobileViewport ? 0.12 : 0.14,
    isMobileViewport ? 8 : 12,
    true
  );
  infinityEmblem = new THREE.Mesh(
    infinityGeometry,
    new THREE.MeshStandardMaterial({
      color: 0x60a5fa,
      emissive: 0x2563eb,
      emissiveIntensity: 0.75,
      roughness: 0.25,
      metalness: 0.85,
      transparent: true,
      opacity: 0.9,
      wireframe: true,
    })
  );
  infinityEmblem.position.y = 2;
  infinityEmblem.rotation.x = Math.PI * 0.12;
  scene.add(infinityEmblem);
};

const onMouseMove = (event) => {
  updatePointer(event.clientX, event.clientY);
};

const onTouchMove = (event) => {
  const touch = event.touches[0];
  if (!touch) return;
  updatePointer(touch.clientX, touch.clientY);
};

const animate = () => {
  if (particles) {
    particles.rotation.y += 0.0005;
    particles.rotation.x += 0.0002;
  }

  if (infinityEmblem) {
    infinityEmblem.rotation.z += 0.004;
    infinityEmblem.rotation.y += 0.008;
  }

  if (camera) {
    camera.position.x += (mouseX * 2 - camera.position.x) * 0.02;
    camera.position.y += (mouseY * 2 + 2 - camera.position.y) * 0.02;
    camera.lookAt(0, 2, 0);
  }

  controls?.update();
  renderer?.render(scene, camera);

  animationFrame = requestAnimationFrame(animate);
};

const onResize = () => {
  if (!camera || !renderer) return;
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
};

onMounted(async () => {
  useMotionBackground.value = shouldUseThree();

  if (useMotionBackground.value) {
    await initThree();
    if (!isMounted) return;
    animate();
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('resize', onResize);
  }
});

onUnmounted(() => {
  isMounted = false;
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('resize', onResize);
  cancelAnimationFrame(animationFrame);

  if (scene) {
    scene.traverse((object) => {
      if (object.geometry) object.geometry.dispose();
      const { material } = object;
      if (!material) return;
      if (Array.isArray(material)) {
        material.forEach((m) => m.dispose());
      } else {
        material.dispose();
      }
    });
  }

  renderer?.dispose();
  controls?.dispose?.();

  if (canvasContainer.value) {
    canvasContainer.value.innerHTML = '';
  }
});
</script>

<style scoped>
.star-background {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.star-background-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom right, #030712, #111827, #0f172a);
}

.star-background-canvas {
  position: absolute;
  inset: 0;
}

.star-background-canvas :deep(canvas) {
  display: block;
  pointer-events: none;
}
</style>
